import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Autocomplete, Button, CircularProgress, TextField, Typography } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import makeStyles from '@mui/styles/makeStyles'
import moment from 'moment-timezone'
import { DateTimePicker as MuiDateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import ParticipantTime from 'src/date/ParticipantTime'
import { useGetFullPatientQuery } from 'src/participant/patientQueries.generated'
import { isCredentialedForParticipant } from 'src/lib/isCredentialedForParticipant'
import useLoadCoachProfiles, { CoachProfileWithCoachID } from 'src/participant/SetCareTeamForm/useLoadCoachProfiles'
import { CallBookingParticipantFragment } from '../../videoCallBookingHeaderQueries.generated'
import useBookCall from '../CoachProfileList/BookCallButton/useBookCall'

interface ManualSchedulingViewProps {
  participant: CallBookingParticipantFragment
  onCloseDialog: () => void
}

const browserTimezone = moment.tz.guess()
const browserTimezoneAbbr = moment.tz(browserTimezone).zoneAbbr()

const localeData = moment.localeData()
const pickerFormat = `${localeData.longDateFormat('L')} [at] ${localeData.longDateFormat(
  'LT'
)} [${browserTimezoneAbbr}]`

const ManualSchedulingView = ({ participant, onCloseDialog }: ManualSchedulingViewProps) => {
  const styles = useStyles()
  const [selectedCoach, setSelectedCoach] = useState<CoachProfileWithCoachID | null>(null)
  const [selectedDateTime, setSelectedDateTime] = useState<moment.Moment | null>(null)

  const { data: patientData, loading: patientLoading } = useGetFullPatientQuery({
    variables: { uid: participant.uid },
  })
  const { stateOfResidence, credentialingHealthPlan } = patientData?.patient ?? {}

  const allCoachProfiles = useLoadCoachProfiles()
  const coachProfiles = useMemo(
    () =>
      patientLoading || stateOfResidence === undefined
        ? []
        : allCoachProfiles.filter((coach) =>
            isCredentialedForParticipant(coach, stateOfResidence, credentialingHealthPlan)
          ),
    [allCoachProfiles, stateOfResidence, credentialingHealthPlan, patientLoading]
  )

  const participantTimeMs = selectedDateTime && selectedDateTime.isValid() ? selectedDateTime.valueOf() : undefined

  const coachEmail = selectedCoach?.email ?? null
  const startTime = selectedDateTime && selectedDateTime.isValid() ? selectedDateTime.toDate() : null

  const { loading, success, bookCall } = useBookCall({
    participant,
    coachEmail,
    startTime,
  })

  const prevSuccessRef = useRef(false)

  useEffect(() => {
    if (prevSuccessRef.current && !success) {
      onCloseDialog()
    }
    prevSuccessRef.current = success
  }, [success, onCloseDialog])

  const isBookingDisabled = !selectedCoach || !startTime || loading || success

  const getButtonContent = () => {
    if (loading) {
      return (
        <>
          <CircularProgress size={16} color="inherit" style={{ marginRight: 8 }} />
          Scheduling...
        </>
      )
    }
    if (success) {
      return (
        <>
          <CheckIcon style={{ marginRight: 8 }} />
          Scheduled
        </>
      )
    }
    return 'Schedule call'
  }

  return (
    <div className={styles.container}>
      <Autocomplete
        options={coachProfiles}
        getOptionLabel={(option) => `${option.firstname} ${option.lastname}${option.title ? ` — ${option.title}` : ''}`}
        value={selectedCoach}
        onChange={(_event, newValue) => setSelectedCoach(newValue)}
        renderInput={(params) => (
          <TextField {...params} label="Select provider" variant="outlined" size="small" fullWidth />
        )}
        isOptionEqualToValue={(option, value) => option.coachID === value.coachID}
        className={styles.field}
      />

      <LocalizationProvider dateAdapter={AdapterMoment}>
        <MuiDateTimePicker
          ampm
          disableMaskedInput
          inputFormat={pickerFormat}
          label="Select date and time"
          minDateTime={moment()}
          value={selectedDateTime}
          onChange={(newValue) => {
            if (newValue && moment(newValue).isValid()) {
              setSelectedDateTime(moment(newValue))
            } else {
              setSelectedDateTime(null)
            }
          }}
          renderInput={(props) => (
            <TextField {...props} fullWidth margin="normal" size="small" variant="outlined" className={styles.field} />
          )}
        />
      </LocalizationProvider>
      {participantTimeMs !== undefined && (
        <Typography variant="body2" className={styles.participantTimeLabel}>
          <ParticipantTime uid={participant.uid} time={participantTimeMs} />
        </Typography>
      )}

      <Button
        color="primary"
        variant="contained"
        disabled={isBookingDisabled}
        onClick={bookCall}
        className={styles.bookButton}
      >
        {getButtonContent()}
      </Button>
    </div>
  )
}

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    padding: theme.spacing(2, 0),
    minWidth: 400,
  },
  field: {
    marginBottom: theme.spacing(1),
  },
  participantTimeLabel: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(-0.5),
    marginBottom: theme.spacing(1),
    fontStyle: 'italic',
  },
  bookButton: {
    marginTop: theme.spacing(2),
    alignSelf: 'center',
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
}))

export default ManualSchedulingView
